import { z } from "zod";
import { TtsVoiceId } from "./tts-voices.ts";

/**
 * Текст готового трека и его разметка по времени.
 *
 * ⚠️ **Отдельным листом от контракта озвучки (`tts.ts`), и это не порядок ради
 * порядка, а вес в чанке ученика.** Расшифровка едет ученику КОПИЕЙ внутри
 * слайда (пункт 10 порядка работ, docs/tts-design.md), то есть `slides.ts`
 * обязан ссылаться на эту схему значением, а `slides.ts` читают все: стартовый
 * чанк, комната урока, архив. Одна такая ссылка на `tts.ts` утащила бы туда
 * весь заказ озвучки целиком: проверено сборкой 06.09.2026, схемы задачи
 * приехали в общий чанк, потому что зодовский `z.object(...)` для сборщика это
 * вызов с неизвестными последствиями, и выбросить неиспользованное из УЖЕ
 * включённого модуля он не берётся.
 *
 * Здесь поэтому лежит ровно то, что нужно слайду, и ни строчкой больше.
 *
 * ⚠️ **Одна вещь всё-таки едет прицепом, и это замерено, а не забыто:** схеме
 * нужен `TtsVoiceId`, поэтому в общий чанк попадает `tts-voices.ts` целиком,
 * вместе с подписями голосов (около 1,2 КБ до сжатия, замер 06.09.2026).
 * Разъединять словарь и идентификатор ради этого не стали: цена ниже шума, а
 * третий лист рядом с двумя стоил бы дороже. Если однажды окажется дорого,
 * резать надо `tts-voices.ts`, а не эту схему.
 */

/**
 * Реплик в диалоге. Учебное аудирование это разговор двоих, а не пьеса.
 *
 * ⚠️ Число одно на две вещи: столько реплик принимает заказ (`TtsRequest`) и
 * столько же строк бывает в расшифровке готового трека, потому что вторая
 * строится по первому. Живёт здесь, а не в `tts.ts`, по причине из шапки: на
 * него ссылается копия расшифровки в слайде.
 */
export const TTS_LINES_HARD_MAX = 60;

/**
 * Реплик в записи, ниже которых по ней не составить вопросов
 * (docs/listening-questions-design.md, 6.1: отказ `transcript_too_short`).
 *
 * ⚠️ Четыре, и число не с потолка: правило 3 рубрики (7.2 проекта) разрешает не
 * больше двух вопросов на одну реплику, значит на двух репликах (а
 * `COMPOSE_LINES_MIN` равен двум) трёх вопросов не собрать никогда. Рубеж стоит
 * ДО вызова модели, потому что иначе он оплачивается дважды, основным вызовом и
 * гейтом, ради заведомого отказа.
 *
 * ⚠️ **Живёт ЗДЕСЬ, а не рядом со схемой вопросов, и это решение с причиной.**
 * Схема вопроса несёт верный ответ и поэтому заперта за `@edu/shared/exercises`
 * (10.2 проекта). Читателей у числа двое, и второму за барьер нельзя: это
 * кнопка «Вопросы к записи» у звукового элемента слайда, которая рисуется
 * только тогда, когда реплик хватает, а сама лишь зовёт ленивый чанк панели.
 * Оставшись за барьером, число заставило бы кнопку либо утащить туда авторский
 * чанк целиком, либо завести вторую копию порога, а копия разошлась бы с
 * сервером молча. По смыслу оно и так дома: это свойство РАСШИФРОВКИ, «сколько
 * в ней реплик», а не свойство схемы вопроса.
 *
 * Наружу за барьер число по-прежнему видно: `listening-questions.ts` его
 * реэкспортирует, и сервер берёт его оттуда же, откуда остальной контракт.
 */
export const LISTENING_LINES_MIN = 4;

/**
 * Реплика в готовом треке: что сказано, чьим голосом и на каком месте.
 *
 * Границы считает СКЛЕЙКА и только она (раздел 9.1 docs/tts-design.md): она
 * знает длительность каждого куска и каждой паузы. По готовому MP3 их не
 * вычисляет ничто, восстановить их потом означает повторный синтез за деньги.
 */
export const TtsTranscriptLine = z.object({
  text: z.string(),
  /**
   * НАШ идентификатор голоса, а не провайдерский (шапка `tts-voices.ts`).
   * Голос из словаря не удаляется никогда, поэтому строка десятилетней
   * давности разбирается этой же схемой.
   */
  voice: TtsVoiceId,
  /**
   * Начало и конец реплики от начала файла, в миллисекундах.
   *
   * ⚠️ **Границы чуть шире слышимых, и это свойство формата, а не погрешность
   * счёта.** Склейка выбрасывает у каждого куска кадр `Info`, а вместе с ним
   * гэплесс-разметку кодировщика: замерено 06.09.2026 на живых файлах
   * провайдера, в каждом куске объявлены задержка 576 отсчётов и хвост от 576
   * до 1216, то есть около 25 мс тишины в начале и до 28 мс в конце. Без
   * разметки плеер их не срезает. Тому, кто подсвечивает строку под звук, знать
   * это надо заранее: подсветка по этим числам включится примерно на 25 мс
   * раньше слышимого слова и погаснет до 28 мс позже. Числа при этом честные, и
   * подгонять их нельзя: они описывают файл, а не речь.
   *
   * ⚠️ **Пауза после реплики в её границы не входит: она принадлежит тишине.**
   * Отсюда следствие, на которое легко наступить: `endMs` последней реплики
   * равен длительности файла ТОЛЬКО когда паузы после неё нет. Пауза там
   * разрешена и доходит до `TTS_PAUSE_MAX_MS`, то есть до пяти секунд хвостовой
   * тишины, и заказывают её осознанно: после вопроса ученику нужно время на
   * ответ. Считать конец файла по последней реплике поэтому нельзя.
   *
   * ⚠️ Отсюда же правило для подсветки: реплика держит её до начала следующей,
   * а не до своего `endMs`. Иначе на каждой паузе подсветка гасла бы на секунды
   * (`features/slides/slide-transcript.ts` в `@edu/web`).
   */
  startMs: z.number().int().nonnegative(),
  endMs: z.number().int().nonnegative(),
});
export type TtsTranscriptLine = z.infer<typeof TtsTranscriptLine>;

/**
 * Текст готового трека, как его отдаёт ручка.
 *
 * ⚠️ **Отдаётся ВЛАДЕЛЬЦУ файла и никому больше.** Ученику эта ручка не
 * отвечает никогда, даже когда этот самый трек стоит у него на слайде:
 * аудирование, показанное вместе с расшифровкой, перестаёт быть аудированием
 * (решение владельца 06.09.2026, раздел 9.1 docs/tts-design.md).
 *
 * ⚠️ Режим «читать и слушать» эту ручку НЕ зовёт и звать не может: текст
 * приезжает ученику копией внутри слайда, которую кладёт туда преподаватель
 * нажатием (`SlideMedia.transcript` в `slides.ts`). Ручка, отдающая текст
 * ученику, была бы обязана заглянуть в документ занятия и убедиться, что показ
 * включён, то есть проверять права по данным, которых у неё нет.
 *
 * Пустой список это «текста нет»: так отвечают треки, собранные до того, как
 * текст начали хранить, и любой другой файл медиатеки.
 */
export const TtsTranscript = z.object({
  fileId: z.string().uuid(),
  lines: z.array(TtsTranscriptLine),
});
export type TtsTranscript = z.infer<typeof TtsTranscript>;
